// Stage 5 Phase-3 — Portal Overview, reference-aligned layout.
//
// Visual target: /app/memory/screenshots/web_portal_overview_sample.html
//
// Layout (desktop ≥ xl):
//   Page header: H1 (project_name → client_name fallback) + meta row
//                (client name · postcode).
//   Grid `xl:grid-cols-3 gap-10 xl:gap-16`:
//     • Left (`xl:col-span-2`): landscape thumbnail + project summary.
//     • Right (`space-y-6`): four stacked tiles — Action Required
//       (highlighted), Current Workstage, Current Design (with idle
//       emerald dot), Upcoming Programme.
//
// Mobile: grid collapses to a single column, tiles stack beneath the
// left column.  All existing portal data bindings and deep-links are
// preserved — fallback resolver, duplicate-row suppression, per-row
// conditional rendering.
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Calendar } from "lucide-react";
import { portalApi, fmtDate, resolveProjectName } from "./portalApi";
import { EmptyState, Spinner } from "./PortalUi";
import { usePageMeta } from "../../components/Layout";
import { API } from "../../lib/api";

export default function PortalOverview() {
    const [data, setData] = useState(null);
    const [err, setErr] = useState("");
    useEffect(() => {
        portalApi.overview()
            .then((r) => setData(r.data))
            .catch((e) => setErr(
                e?.response?.data?.detail || "Unavailable"));
    }, []);

    const projectName = resolveProjectName(data?.project);
    usePageMeta({
        title: projectName
            ? `${projectName} | Studio G4 Portal`
            : "Overview | Studio G4 Portal",
    });

    if (err) {
        return (
            <div className="py-8" data-testid="portal-overview-err">
                <EmptyState heading="Overview unavailable" note={err} />
            </div>
        );
    }
    if (!data) return <Spinner testid="portal-overview-loading" />;
    const p = data.project;

    return (
        <div className="relative" data-testid="portal-overview">
            {/* Subtle top gradient wash, per reference. */}
            <div className="absolute top-0 left-0 right-0 h-96 bg-gradient-to-b from-zinc-200/50 to-transparent -z-10 pointer-events-none" />

            <div className="w-full py-8 md:py-12 lg:py-16">
                <PageHead project={p} displayName={projectName} />

                <div className="grid grid-cols-1 xl:grid-cols-3 gap-10 xl:gap-16">
                    {/* -------- LEFT (thumbnail + summary) ------------- */}
                    <div className="xl:col-span-2 space-y-10"
                         data-testid="portal-overview-hero">
                        <ThumbnailBlock project={p} displayName={projectName} />
                        <SummaryBlock summary={p.project_summary} />
                    </div>

                    {/* -------- RIGHT (status tiles) ------------------- */}
                    <aside className="space-y-6"
                           data-testid="portal-overview-tiles">
                        <ActionRequiredTile
                            count={data.outstanding_actions_count || 0} />
                        <CurrentWorkstageTile
                            n={p.workstage}
                            label={p.workstage_label} />
                        <CurrentDesignTile issue={data.current_design_issue} />
                        <UpcomingProgrammeTile
                            items={data.programme_snapshot || []} />
                    </aside>
                </div>
            </div>
        </div>
    );
}


// ---------------------------------------------------------------------
// Fallback resolver lives in portalApi.js so PortalLayout can share it.
// ---------------------------------------------------------------------


// ---------------------------------------------------------------------
// Page header — H1 + meta row (client name · postcode).  The row
// suppresses the client-name node when it would duplicate the H1
// (strict trim equality — preserves the information-adds-value rule).
// ---------------------------------------------------------------------
function PageHead({ project, displayName }) {
    const dn = (displayName || "").trim();
    const cn = (project.client_name || "").trim();
    // Prefer the full property address; fall back to postcode when
    // the address is unset.  Both empty ⇒ meta item hidden.
    const addr = (project.property_address || "").trim()
        || (project.postcode || "").trim();
    const showClientName = cn && cn !== dn;

    return (
        <header className="mb-12">
            <h1 className="text-3xl md:text-4xl font-light text-zinc-900 tracking-tight mb-3 font-manrope break-words"
                data-testid="portal-overview-project-name">
                {displayName || "Your project"}
            </h1>
            {(showClientName || addr) && (
                <div className="flex flex-col gap-1 text-zinc-500">
                    {showClientName && (
                        <span className="text-base font-medium text-zinc-600"
                              data-testid="portal-overview-client-name">
                            {project.client_name}
                        </span>
                    )}
                    {addr && (
                        <span className="text-sm" data-testid="portal-overview-address">{addr}</span>
                    )}
                </div>
            )}
        </header>
    );
}


// ---------------------------------------------------------------------
// Thumbnail — landscape (~500px tall), rounded-3xl, subtle hover
// transform.  Streams via the admin-drawer-wired endpoint when the
// admin has uploaded a thumbnail; otherwise the block collapses.
// ---------------------------------------------------------------------
function ThumbnailBlock({ project, displayName }) {
    if (!project.project_thumbnail) return null;
    return (
        <div className="rounded-3xl overflow-hidden shadow-xl shadow-zinc-200/50 border border-zinc-200/60 group relative"
             data-testid="portal-overview-thumbnail-wrap">
            <div className="absolute inset-0 bg-zinc-900/10 group-hover:bg-transparent transition-colors duration-500 z-10" />
            <img src={`${API}/portal/project-thumbnail`}
                 alt={displayName || "Project thumbnail"}
                 data-testid="portal-overview-thumbnail"
                 className="w-full h-[460px] md:h-[575px] object-cover transition-transform duration-700 group-hover:scale-105" />
        </div>
    );
}


// ---------------------------------------------------------------------
// Summary — admin-authored long-form description.  Collapses when
// unset.  `whitespace-pre-wrap` preserves paragraph breaks.
// ---------------------------------------------------------------------
function SummaryBlock({ summary }) {
    const s = (summary || "").trim();
    if (!s) return null;
    return (
        <div className="pr-0 xl:pr-8">
            <p className="text-zinc-600 text-base leading-relaxed font-light whitespace-pre-wrap"
               data-testid="portal-overview-summary">
                {s}
            </p>
        </div>
    );
}


// ---------------------------------------------------------------------
// Tiles — each a white rounded-3xl card.  TileFrame centralises the
// shared chrome; the highlighted "Action Required" tile overrides with
// its own accent styling.
// ---------------------------------------------------------------------
function TileFrame({ label, testid, children, className = "" }) {
    return (
        <div className={`bg-white rounded-3xl p-8 shadow-sm shadow-zinc-200/50 border border-zinc-100 hover:shadow-md transition-shadow ${className}`}
             data-testid={testid}>
            <h2 className="text-xs font-bold tracking-widest text-zinc-400 uppercase mb-4 font-manrope">
                {label}
            </h2>
            {children}
        </div>
    );
}


function ActionRequiredTile({ count }) {
    return (
        <div className="bg-white rounded-3xl p-8 shadow-lg shadow-[#A8543F]/5 border border-[#F2E1DB] relative overflow-hidden group"
             data-testid="portal-overview-actions-card">
            {/* Terracotta decorative strip, per reference. */}
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#D38C78] to-[#A8543F]" />
            <h2 className="text-xs font-bold tracking-widest text-zinc-400 uppercase mb-4 font-manrope">
                Action Required
            </h2>
            <div className="flex items-end gap-4 mb-4">
                <span className="text-6xl font-light text-[#A8543F] leading-none font-manrope"
                      data-testid="portal-overview-outstanding">
                    {count}
                </span>
                <span className="text-zinc-600 font-medium pb-1">
                    {count === 1 ? "Item" : "Items"} awaiting your input
                </span>
            </div>
            <Link to="/portal/actions"
                  data-testid="portal-overview-goto-actions"
                  className="inline-flex items-center gap-2 text-[#A8543F] font-medium hover:text-[#8B4535] transition-colors mt-2 text-sm">
                View actions
                <ArrowRight size={16}
                            className="transition-transform group-hover:translate-x-1" />
            </Link>
        </div>
    );
}


function CurrentWorkstageTile({ n, label }) {
    const num = Number.isFinite(n) ? n : null;
    return (
        <TileFrame label="Current Workstage"
                   testid="portal-overview-workstage-card">
            <h3 className="text-2xl font-semibold text-zinc-900 mb-1 font-manrope"
                data-testid="portal-overview-workstage-n">
                {num ? `Workstage ${num}` : "—"}
            </h3>
            {label && (
                <p className="text-zinc-500"
                   data-testid="portal-overview-workstage-label">
                    {label}
                </p>
            )}
        </TileFrame>
    );
}


function CurrentDesignTile({ issue }) {
    return (
        <div className="bg-white rounded-3xl p-8 shadow-sm shadow-zinc-200/50 border border-zinc-100 hover:shadow-md transition-shadow flex items-start justify-between gap-4"
             data-testid="portal-overview-design-card">
            <div className="min-w-0 flex-1">
                <h2 className="text-xs font-bold tracking-widest text-zinc-400 uppercase mb-4 font-manrope">
                    Current Design
                </h2>
                {issue ? (
                    <>
                        <p className="text-zinc-900 font-medium font-manrope">
                            {issue.title}
                        </p>
                        <p className="text-zinc-500 text-sm mt-1">
                            Feedback due {fmtDate(issue.feedback_due_date)}
                        </p>
                        <Link to="/portal/design"
                              data-testid="portal-overview-goto-design"
                              className="mt-3 inline-flex items-center gap-2 text-[#A8543F] font-medium hover:text-[#8B4535] transition-colors text-sm">
                            View design
                            <ArrowRight size={16} />
                        </Link>
                    </>
                ) : (
                    <p className="text-zinc-900 font-medium font-manrope">
                        No current design issue.
                    </p>
                )}
            </div>
            {!issue && (
                /* Idle emerald indicator — purely visual, per reference. */
                <div className="mt-1 flex items-center justify-center w-8 h-8 rounded-full bg-emerald-50 flex-shrink-0"
                     data-testid="portal-overview-design-idle-dot"
                     aria-hidden="true">
                    <div className="w-2.5 h-2.5 rounded-full bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.4)]" />
                </div>
            )}
        </div>
    );
}


function UpcomingProgrammeTile({ items }) {
    const next = items.length > 0 ? items[0] : null;
    return (
        <TileFrame label="Upcoming Programme"
                   testid="portal-overview-programme"
                   className="group">
            {next ? (
                <>
                    <h3 className="text-xl font-semibold text-zinc-900 mb-1 font-manrope"
                        data-testid={`portal-overview-prog-${next.id}`}>
                        {next.title}
                    </h3>
                    <p className="text-zinc-500 mb-6 flex items-center gap-2">
                        <Calendar size={16} className="opacity-70" />
                        {fmtDate(next.target_at)}
                    </p>
                </>
            ) : (
                <p className="text-zinc-500 mb-6">No upcoming items.</p>
            )}
            <Link to="/portal/programme"
                  data-testid="portal-overview-goto-programme"
                  className="inline-flex items-center gap-2 text-zinc-900 font-medium hover:text-[#A8543F] transition-colors text-sm">
                Full programme
                <ArrowRight size={16}
                            className="transition-transform group-hover:translate-x-1" />
            </Link>
        </TileFrame>
    );
}
